import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import { formatBytes } from '../../../services/uploadedDocumentsService';
import DocumentStatusBadge from '../../../components/documents/DocumentStatusBadge';
import { normalizeDocumentStatus } from '../../../utils/documentStatusMeta';
import { DOCUMENT_TITLE_WRAP_CLASS } from '../../../utils/documentTitle';

function fileSizeBytes(document) {
  const raw = document?.file_size_bytes ?? document?.fileSizeBytes;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const UploadedDocumentTableRow = ({
  document,
  onDownload,
  onView,
  onSign,
  onDelete,
  onCancelSignature,
  onViewSignatureStatus,
  actionsDisabled = false,
}) => {
  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const status = normalizeDocumentStatus(document.status);
  const isPendingSignature = status === 'PENDING_SIGNATURE';
  const isSigned = status === 'SIGNED';

  const canSendToSignature =
    status === 'PDF_UPLOADED' || status === 'PDF_GENERATED';
  const canDownload =
    status === 'PDF_UPLOADED' ||
    status === 'PDF_GENERATED' ||
    isPendingSignature ||
    isSigned;

  const sizeBytes = fileSizeBytes(document);

  return (
    <tr
      className={cn(
        'list-item-hover border-l-4 border-primary/30',
        isSigned && 'bg-amber-500/5'
      )}
    >
      <td className="px-4 md:px-6 py-4 max-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 bg-muted/50">
            <Icon name="FileText" size={20} className="text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'text-sm font-medium text-foreground font-body',
                DOCUMENT_TITLE_WRAP_CLASS
              )}
              title={document.title || 'Documento'}
            >
              {document.title || 'Documento'}
            </p>
            <p className="text-xs text-muted-foreground font-caption mt-0.5 md:hidden">
              {sizeBytes != null ? formatBytes(sizeBytes) : '—'}
            </p>
          </div>
        </div>
      </td>

      <td className="px-4 md:px-6 py-4 hidden md:table-cell">
        <p className="text-sm text-foreground font-body tabular-nums">
          {sizeBytes != null ? formatBytes(sizeBytes) : '—'}
        </p>
      </td>

      <td className="px-4 md:px-6 py-4 hidden md:table-cell">
        <DocumentStatusBadge status={document.status} />
      </td>

      <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
        <p className="text-sm text-foreground font-body">{formatDateTime(document.created_at)}</p>
      </td>

      <td className="px-4 md:px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {isPendingSignature ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => onViewSignatureStatus?.(document)}
                disabled={actionsDisabled}
                className="transition-transform hover:scale-110"
                aria-label="Status das assinaturas"
                title="Status das assinaturas"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Ban"
                onClick={() => onCancelSignature?.(document)}
                disabled={actionsDisabled}
                className="transition-transform hover:scale-110"
                aria-label="Cancelar assinatura"
                title="Cancelar assinatura"
              />
            </>
          ) : isSigned ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => onViewSignatureStatus?.(document)}
                disabled={actionsDisabled}
                className="transition-transform hover:scale-110"
                aria-label="Status das assinaturas"
                title="Status das assinaturas"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                onClick={() => onDownload(document)}
                disabled={actionsDisabled || !canDownload}
                className="transition-transform hover:scale-110"
                aria-label="Baixar PDF"
                title="Baixar PDF"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={() => onDelete(document)}
                disabled={actionsDisabled}
                className="text-destructive hover:text-destructive transition-transform hover:scale-110"
                aria-label="Excluir documento"
                title="Excluir documento"
              />
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => onView?.(document)}
                disabled={actionsDisabled || !canDownload}
                className="transition-transform hover:scale-110"
                aria-label="Visualizar documento"
                title="Visualizar documento"
              />

              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                onClick={() => onDownload(document)}
                disabled={actionsDisabled || !canDownload}
                className="transition-transform hover:scale-110"
                aria-label="Baixar PDF"
                title="Baixar PDF"
              />

              <Button
                variant="ghost"
                size="sm"
                iconName="PenTool"
                onClick={() => onSign(document)}
                disabled={actionsDisabled || !canSendToSignature}
                className="transition-transform hover:scale-110"
                aria-label="Posicionar campos de assinatura"
                title="Posicionar campos de assinatura"
              />

              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={() => onDelete(document)}
                disabled={actionsDisabled}
                className="text-destructive hover:text-destructive transition-transform hover:scale-110"
                aria-label="Excluir documento"
                title="Excluir documento"
              />
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default UploadedDocumentTableRow;
